/**
 * C.12 — NIPE control context.
 *
 * Lightweight imperative surface that Start menu items + the taskbar
 * tray icon use to toggle NIPE without knowing the overlay's internal
 * state. The overlay owns the panelOpen state and registers an
 * imperative API on mount; consumers call `nipe.open()` / `toggle()`
 * etc.
 *
 * Nesting: __root.tsx wraps the portal route in its own Win95Desktop,
 * which renders ANOTHER Win95Desktop. We treat the OUTER instance as
 * canonical (the inner one sits inside the outer's display:none
 * Outlet wrapper and is invisible). The IsOuter context flag lets
 * inner NipeOverlay / NipeTrayIcon bail.
 *
 * No window registration here — NIPE is an overlay, not a window.
 */

import { createContext, useContext, useMemo, useRef, type ReactNode } from 'react';

export interface NipeControl {
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const NOOP_CONTROL: NipeControl = {
  open: () => {},
  close: () => {},
  toggle: () => {},
};

const NOOP_SET_CONTROL = (_c: NipeControl): void => {
  void _c;
};

interface NipeControlContextValue {
  control: NipeControl;
  setControl: (c: NipeControl) => void;
  /** Marker the inner provider reads to detect "I'm nested, bail". */
  inProvider: boolean;
}

const NipeControlContext = createContext<NipeControlContextValue>({
  control: NOOP_CONTROL,
  setControl: NOOP_SET_CONTROL,
  inProvider: false,
});

export function NipeControlProvider({ children }: { children: ReactNode }) {
  const controlRef = useRef<NipeControl>(NOOP_CONTROL);
  const parent = useContext(NipeControlContext);

  const value = useMemo<NipeControlContextValue>(
    () => ({
      control: {
        open: () => controlRef.current.open(),
        close: () => controlRef.current.close(),
        toggle: () => controlRef.current.toggle(),
      },
      setControl: (c: NipeControl) => {
        controlRef.current = c;
      },
      inProvider: true,
    }),
    [],
  );

  // If a parent NipeControlProvider is already present, pass through —
  // the outer provider stays canonical so the overlay mounts above the
  // (visible) outer WindowLayer rather than inside the inner Outlet.
  if (parent.inProvider) {
    return <>{children}</>;
  }
  return <NipeControlContext.Provider value={value}>{children}</NipeControlContext.Provider>;
}

export function useNipeControl(): NipeControl {
  return useContext(NipeControlContext).control;
}

/** Internal hook for the overlay to register its imperative API. */
export function useNipeControlRegistrar(): (c: NipeControl) => void {
  return useContext(NipeControlContext).setControl;
}

// Module-level guard: the first NipeOverlay/NipeTrayIcon mounted owns
// rendering. Reset on tab navigation by checking still-mounted refs.
// We expose `useIsOuterNipe` returning true for the outer instance.

const OUTER_OWNER: { current: object | null } = { current: null };

export function useOuterNipeClaim(): boolean {
  const tokenRef = useRef<object>({});
  if (OUTER_OWNER.current === null) {
    OUTER_OWNER.current = tokenRef.current;
  }
  const owns = OUTER_OWNER.current === tokenRef.current;
  // If we are not the owner, but the owner has been GC'd or unmounted,
  // we cannot detect that here — but for our case the outer Win95Desktop
  // stays mounted for the page lifetime, so this is fine.
  return owns;
}
